import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { licenseTypesAPI } from "../../services/api";

export interface LicenseType {
	_id: string;
	// Stable machine identifier. Immutable once created, so the seeder and the
	// backfill keep finding the same type after a rename.
	key: string;
	name: string;
	description?: string;
	monthlyPrice: number;
	features: string[];
	isActive: boolean;
	createdAt: string;
}

export interface LicenseFeature {
	key: string;
	label: string;
}

interface LicenseTypesResponse {
	licenseTypes: LicenseType[];
	total: number;
}

interface LicenseTypesState {
	licenseTypes: LicenseType[];
	// Catalog of assignable feature flags, served by the backend so the two
	// sides cannot drift apart.
	features: LicenseFeature[];
	loading: boolean;
	error: string | null;
	total: number;
	page: number;
	limit: number;
}

const initialState: LicenseTypesState = {
	licenseTypes: [],
	features: [],
	loading: false,
	error: null,
	total: 0,
	page: 1,
	limit: 25,
};

export const fetchLicenseTypes = createAsyncThunk(
	"licenseTypes/fetchLicenseTypes",
	async (
		params: { page?: number; limit?: number; isActive?: boolean } = {},
		{ rejectWithValue }
	) => {
		try {
			const response = await licenseTypesAPI.getAll(params);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				// Don't treat 401 errors as failures - they're handled by token refresh
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch license types"
				);
			}
			return rejectWithValue("Failed to fetch license types");
		}
	}
);

export const fetchLicenseFeatures = createAsyncThunk(
	"licenseTypes/fetchLicenseFeatures",
	async (_: void, { rejectWithValue }) => {
		try {
			const response = await licenseTypesAPI.getFeatures();
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to fetch license features"
				);
			}
			return rejectWithValue("Failed to fetch license features");
		}
	}
);

export const createLicenseType = createAsyncThunk(
	"licenseTypes/createLicenseType",
	async (
		licenseTypeData: {
			key: string;
			name: string;
			description?: string;
			monthlyPrice: number;
			features?: string[];
			isActive?: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await licenseTypesAPI.create(licenseTypeData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to create license type"
				);
			}
			return rejectWithValue("Failed to create license type");
		}
	}
);

export const updateLicenseType = createAsyncThunk(
	"licenseTypes/updateLicenseType",
	async (
		{
			id,
			...licenseTypeData
		}: {
			id: string;
			name?: string;
			description?: string;
			monthlyPrice?: number;
			features?: string[];
			isActive?: boolean;
		},
		{ rejectWithValue }
	) => {
		try {
			const response = await licenseTypesAPI.update(id, licenseTypeData);
			return response.data;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to update license type"
				);
			}
			return rejectWithValue("Failed to update license type");
		}
	}
);

export const deleteLicenseType = createAsyncThunk(
	"licenseTypes/deleteLicenseType",
	async (id: string, { rejectWithValue }) => {
		try {
			await licenseTypesAPI.delete(id);
			return id;
		} catch (error: unknown) {
			if (error && typeof error === "object" && "response" in error) {
				const axiosError = error as {
					response?: { status?: number; data?: { message?: string } };
				};
				if (axiosError.response?.status === 401) {
					throw error;
				}
				return rejectWithValue(
					axiosError.response?.data?.message || "Failed to delete license type"
				);
			}
			return rejectWithValue("Failed to delete license type");
		}
	}
);

const licenseTypesSlice = createSlice({
	name: "licenseTypes",
	initialState,
	reducers: {
		clearError: (state) => {
			state.error = null;
		},
		setPage: (state, action: PayloadAction<number>) => {
			state.page = action.payload;
		},
		setLimit: (state, action: PayloadAction<number>) => {
			state.limit = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder
			// Fetch license types
			.addCase(fetchLicenseTypes.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(fetchLicenseTypes.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload.data as LicenseTypesResponse;
				state.licenseTypes = data?.licenseTypes || [];
				state.total = data?.total || 0;
			})
			.addCase(fetchLicenseTypes.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Fetch feature catalog
			.addCase(fetchLicenseFeatures.fulfilled, (state, action) => {
				const data = action.payload.data as { features: LicenseFeature[] };
				state.features = data?.features || [];
			})
			.addCase(fetchLicenseFeatures.rejected, (state, action) => {
				state.error = action.payload as string;
			})
			// Create license type
			.addCase(createLicenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createLicenseType.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload.data as { licenseType: LicenseType };
				if (data) {
					state.licenseTypes.push(data.licenseType);
					state.licenseTypes.sort((a, b) => a.name.localeCompare(b.name));
					state.total += 1;
				}
			})
			.addCase(createLicenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Update license type
			.addCase(updateLicenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(updateLicenseType.fulfilled, (state, action) => {
				state.loading = false;
				const data = action.payload.data as { licenseType: LicenseType };
				if (data) {
					const index = state.licenseTypes.findIndex(
						(licenseType) => licenseType._id === data.licenseType._id
					);
					if (index !== -1) {
						state.licenseTypes[index] = data.licenseType;
					}
				}
			})
			.addCase(updateLicenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			})
			// Delete license type
			.addCase(deleteLicenseType.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(deleteLicenseType.fulfilled, (state, action) => {
				state.loading = false;
				state.licenseTypes = state.licenseTypes.filter(
					(licenseType) => licenseType._id !== action.payload
				);
				state.total -= 1;
			})
			.addCase(deleteLicenseType.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload as string;
			});
	},
});

export const { clearError, setPage, setLimit } = licenseTypesSlice.actions;
export default licenseTypesSlice.reducer;
